/* eslint-disable no-undef */
import React from 'react';
import { hasDebugFlag } from './isDebugQuery';

export const isBrowser = typeof window !== 'undefined';

export const debug = function debug(...args) {
  // Enable debug logs only for the explicit `?debug=linkparams` opt-in
  // (renamed from the old `?debug=1`). Substring matches like
  // `?gtm_debug=<id>` (Google Tag Manager Preview mode) must not trigger
  // this. See `isDebugQuery.js`.
  if (typeof window !== 'undefined' && hasDebugFlag(window?.location?.search)) {
    // eslint-disable-next-line no-console
    console?.log?.apply(this, args);
  }
};

// Converts array of option sets into an object map
export const createOptionMap = (optionSets, language) => {
  // Handle null/undefined optionSets gracefully
  if (!optionSets || !Array.isArray(optionSets)) {
    return {};
  }

  let filteredOptionSets = optionSets;

  if (language !== undefined) {
    filteredOptionSets = optionSets.filter(
      (node) => node.language === language
    );
  }

  return filteredOptionSets.reduce((acc, optionSet) => {
    const newAcc = { ...acc };

    // Handle null/undefined optionSet or options
    if (!optionSet || !optionSet.options || !Array.isArray(optionSet.options)) {
      return newAcc;
    }

    optionSet.options.forEach((option) => {
      let value;

      if (option.__typename === 'ContentfulOption') {
        value = option.value?.value
          ? option.value?.value : option.valueNoTranslate?.valueNoTranslate;
      } else if (option.__typename === 'ContentfulOptionJson') {
        let jsonData = option.value?.internal?.content;
        if (jsonData) {
          // Smartling is encoding & as &amp; in JSON data
          jsonData = jsonData.replace(/&amp;/g, '&');
          value = JSON.parse(jsonData);
        }
      }

      newAcc[option.key] = value;
    });
    return newAcc;
  }, {});
};

export const getFirst = (arr) => {
  if (arr !== null && Array.isArray(arr) && arr.length > 0) {
    return arr[0];
  }
  return null;
};

export const flattenArray = (array) => array.reduce((acc, curr) => acc.concat(curr), []);

// Needle must be a regex with a capture group
// parentIndex carries the path through any array recursion so that generated
// keys are unique across siblings. Without the item index, multiple string
// items in the same array (e.g. text broken up by <Newline /> tokens) would
// each emit components with the same key and break React reconciliation.
export const replaceWithComponent = (needle, haystack, Component, keyPrefix, parentIndex = '0') => {
  if (Array.isArray(haystack)) {
    return flattenArray(
      haystack.map(
        (item, itemIndex) => replaceWithComponent(
          needle,
          item,
          Component,
          keyPrefix,
          `${parentIndex}-${itemIndex}`
        )
      )
    );
  }
  if (typeof haystack !== 'string') {
    return haystack;
  }

  return haystack.split(needle)
    .map((value, index) => {
      if (index % 2 !== 0) {
        // eslint-disable-next-line react/no-array-index-key
        return <Component key={`${keyPrefix}-${parentIndex}-${index}`} value={value} />;
      }
      return value;
    });
};

export const removeNoTranslateWrapper = (str) => {
  if (typeof str === 'string') {
    // We use {{{...}}} to mark text as a non-translatable placeholder in Smartling.
    // Smartling also uses <span translate="no">...</span> and <notranslate>...</notranslate>
    // to mark text as non-translatable
    return str
      .replace(/{{{/g, '').replace(/}}}/g, '')
      .replace(/<span translate="no">(.*?)<\/span>/gi, '$1')
      .replace(/<notranslate>(.*?)<\/notranslate>/gi, '$1');
  }

  return str;
};

export const outputIfNoVariables = (str) => {
  if (typeof str === 'string') {
    if (str.match(/{{(.*?)}}/g)) {
      return null;
    }

    return str;
  }

  return null;
};

export const objectDeepReplace = (obj, needle, replacement) => {
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map((item) => objectDeepReplace(item, needle, replacement));
    }

    return Object.keys(obj).reduce((acc, key) => {
      const newAcc = { ...acc };
      newAcc[key] = objectDeepReplace(obj[key], needle, replacement);
      return newAcc;
    }, {});
  }

  if (typeof obj === 'string') {
    return obj.replace(needle, replacement);
  }

  return obj;
};

export const isVisitorBot = () => {
  if (!isBrowser) {
    return true;
  }

  const botRegex = /(googlebot|googleweblight|-google|google-|semrush|ahrefs|spiceworks|fuzz|wget|curl|python|httpunit|nutch|slurp|teoma|convera|prtg|go-http|bot|spider|crawl)/;
  return botRegex.test(window.navigator.userAgent.toLowerCase());
};

export const isVisitorMSIE = () => {
  if (!isBrowser) {
    return false;
  }

  return /msie/i.test(window.navigator.userAgent);
};

export const getBrowserLanguage = () => {
  if (!isBrowser) {
    return null;
  }

  return window.navigator.language;
};

export const sortQueryParams = (a, b) => {
  const moveParamsToEnd = ['gclid', 'msclkid', 'lqid'];

  if (moveParamsToEnd.includes(a)) {
    return 1;
  }

  if (moveParamsToEnd.includes(b)) {
    return -1;
  }

  return 0;
};

// Internal-only userContext keys and analytics linker params we never want
// to surface in outbound URLs (e.g. trial links, CTAs). `_expires_at` is the
// persistent-cookie expiry timestamp; `_gl` is the Google Analytics
// cross-domain linker token, which is long and not useful in our links.
// ajs_* and optid are for cross-origin tracking
const URL_EXCLUDED_PARAMS = ['_expires_at', '_gl', 'ajs_aid', 'ajs_uid', 'optid'];

// Some ad / campaign tracking templates emit PHP/array-style query params such
// as `utm_source[]=google` (frequently repeated), `utm_source[0]=google` or
// `utm_source[sub]=google`. Depending on where the data comes from these
// surface in different shapes:
//   - from parseQueryParams: a bracketed KEY (`utm_source[]`) with a scalar value
//   - from a stale cookie:   a clean key (`utm_source`) with an ARRAY value, or a
//                            nested OBJECT (older builds parsed the URL with qs)
// All of these corrupt attribution downstream: a bracketed key never matches
// `utm_source`, and an array/object value re-serializes back out with
// `[]` / `[n]` / `[sub]` notation. That malformed `utm_source[]=...` is exactly
// what lands in `spid_trial_src_ref__c`, which prevents Salesforce from parsing
// the lead source, and it also leaves the dedicated `latest_utm_*` form fields
// empty. This collapses every shape back to a single clean `key=value` pair (the
// first usable string wins). A clean key that is already present is never
// overwritten by a bracketed duplicate.
const ARRAY_PARAM_KEY_RE = /(\[[^\]]*\])+$/;

// Attribution params must end up as plain, non-empty strings. Anything else —
// `undefined`, `null`, numbers, booleans, or nested objects — is treated as
// "no usable value" so junk never reaches the cookie or a Salesforce/HubSpot
// field (where it would re-serialize with brackets and re-break lead-source
// parsing).
const hasUsableValue = (value) => typeof value === 'string' && value.length > 0;

const firstNonEmptyScalar = (value) => {
  if (Array.isArray(value)) {
    return value.find(hasUsableValue) ?? '';
  }
  return typeof value === 'string' ? value : '';
};

export const normalizeArrayParams = (params) => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return params;
  }
  return Object.keys(params).reduce((acc, key) => {
    const baseKey = key.replace(ARRAY_PARAM_KEY_RE, '');
    const isBracketed = baseKey !== key;
    // A bracketed duplicate must not clobber a clean value we already kept.
    if (isBracketed && hasUsableValue(acc[baseKey])) {
      return acc;
    }
    acc[baseKey] = firstNonEmptyScalar(params[key]);
    return acc;
  }, {});
};

// Parses a query string (with or without the leading `?`) into a flat object of
// string values using the platform URLSearchParams. Repeated keys collapse to
// the first non-empty value, and bracketed keys (`utm_source[]`) are kept as-is
// so normalizeArrayParams can fold them into the clean key.
export const parseQueryParams = (search) => {
  const result = {};
  new URLSearchParams(search || '').forEach((value, key) => {
    if (!hasUsableValue(result[key])) {
      result[key] = value;
    }
  });
  return result;
};

// RFC 3986 percent-encoding. encodeURIComponent already leaves the unreserved
// set alone but also skips `!'()*`, which must be encoded to stay byte-for-byte
// compatible with the query strings the site has always emitted.
const encodeQueryComponent = (value) => encodeURIComponent(value)
  .replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);

// Serializes a flat params object to a query string (no leading `?`).
// `undefined` values are omitted, `null` and `''` serialize as a bare `key=`,
// numbers/booleans are stringified, arrays collapse to their first usable
// string and nested objects are dropped, so bracket notation can never leak
// back out. `sort` is an optional key comparator (see sortQueryParams).
export const stringifyQueryParams = (params, { sort } = {}) => {
  const keys = Object.keys(params || {});
  if (sort) {
    keys.sort(sort);
  }
  return keys.reduce((parts, key) => {
    let value = params[key];
    if (Array.isArray(value)) {
      value = firstNonEmptyScalar(value);
    }
    if (value === undefined || (typeof value === 'object' && value !== null)) {
      return parts;
    }
    parts.push(`${encodeQueryComponent(key)}=${value === null ? '' : encodeQueryComponent(String(value))}`);
    return parts;
  }, []).join('&');
};

// Splits a URL (or a bare "?query#hash" suffix) into its path, query string and hash
export const splitUrl = (url = '') => {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf('?');

  return {
    path: queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex),
    query: queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1),
    hash
  };
};

export const mergeQueryParams = (url, params) => {
  const { path, query, hash } = splitUrl(url);
  const existingParams = query ? parseQueryParams(query) : {};
  const newParams = { ...existingParams, ...params, platform: 'web' };
  URL_EXCLUDED_PARAMS.forEach((key) => {
    if (key in newParams) delete newParams[key];
  });
  const newSearchString = stringifyQueryParams(newParams, { sort: sortQueryParams });
  return `${path}?${newSearchString}${hash}`;
};

const getQueryParamKey = (param) => {
  const [key] = param.split('=');
  try {
    return decodeURIComponent(key.replace(/\+/g, ' '));
  } catch (e) {
    return key;
  }
};

// Applies a raw query string on top of a URL. Keys in `overrides` replace existing
// keys in place, new keys are appended, and params are kept verbatim so bare keys
// such as "?helloworld" stay bare.
export const overrideQueryParams = (url, overrides) => {
  const overrideList = (overrides || '').replace(/^[?&]/, '').split('&').filter(Boolean);

  if (!url || overrideList.length === 0) {
    return url;
  }

  const overridesByKey = new Map();
  overrideList.forEach((param) => {
    const key = getQueryParamKey(param);
    overridesByKey.set(key, [...(overridesByKey.get(key) || []), param]);
  });

  const { path, query, hash } = splitUrl(url);
  const applied = new Set();
  const params = [];

  query.split('&').filter(Boolean).forEach((param) => {
    const key = getQueryParamKey(param);
    if (!overridesByKey.has(key)) {
      params.push(param);
    } else if (!applied.has(key)) {
      params.push(...overridesByKey.get(key));
      applied.add(key);
    }
  });

  overridesByKey.forEach((paramsForKey, key) => {
    if (!applied.has(key)) {
      params.push(...paramsForKey);
    }
  });

  return `${path}?${params.join('&')}${hash}`;
};

// Applies a "?query#hash" suffix written after a {{link-...}} variable to its resolved URL
export const applyLinkSuffix = (url, suffix) => {
  if (!url || !suffix) {
    return url;
  }

  const { query, hash } = splitUrl(suffix);
  const merged = splitUrl(overrideQueryParams(url, query));
  const queryString = merged.query ? `?${merged.query}` : '';

  return `${merged.path}${queryString}${hash || merged.hash}`;
};

// Removes whole query parameters from a URL until the query string size
// (in bytes, as it will appear in the HTTP request line) is at or below
// the supplied limit. Each iteration drops the single longest parameter,
// measured as the raw `key=value` byte length (including any percent-encoding
// already present in the URL). Whole parameters are removed -- partial
// values are never truncated. Surviving parameters keep their original
// relative order.
//
// Tie-breaking: when multiple params share the current maximum length, the
// LATER occurrence in the query string is dropped first, on the theory that
// earlier params (e.g. utm_source) are typically more attribution-critical.
//
// The URL is returned unchanged when there is no query string or when the
// query string already fits within the limit. Any `#fragment` is preserved.
export const limitQueryStringSize = (url, limit) => {
  if (typeof url !== 'string' || typeof limit !== 'number' || limit < 0) {
    return url;
  }
  const questionIdx = url.indexOf('?');
  if (questionIdx === -1) return url;

  const base = url.slice(0, questionIdx);
  const afterQ = url.slice(questionIdx + 1);
  const hashIdx = afterQ.indexOf('#');
  const search = hashIdx === -1 ? afterQ : afterQ.slice(0, hashIdx);
  const fragment = hashIdx === -1 ? '' : afterQ.slice(hashIdx);

  if (search.length <= limit) return url;

  const pairs = search.split('&').map((raw) => ({ raw }));
  let total = search.length;

  while (total > limit && pairs.length > 0) {
    let dropAt = 0;
    let maxLen = pairs[0].raw.length;
    for (let i = 1; i < pairs.length; i += 1) {
      if (pairs[i].raw.length >= maxLen) {
        dropAt = i;
        maxLen = pairs[i].raw.length;
      }
    }
    const [removed] = pairs.splice(dropAt, 1);
    total -= removed.raw.length;
    if (pairs.length > 0) total -= 1;
  }

  const newSearch = pairs.map((p) => p.raw).join('&');
  if (newSearch === '') return `${base}${fragment}`;
  return `${base}?${newSearch}${fragment}`;
};

// Returns a shallow copy of `obj` whose `JSON.stringify` length is at or below
// `limit`, dropping whole non-protected entries -- largest serialized
// `"key":value` contribution first -- until it fits or only protected keys
// remain. This is the object/cookie analogue of `limitQueryStringSize`: it
// bounds the serialized size of the `userContext` / `userContextPersistent`
// cookies so unbounded attribution accrual cannot push the request `Cookie`
// header past the origin's limit (HTTP 400 "Bad Request"). Values are never
// truncated; on a size tie the later-inserted key is dropped first (earlier
// keys are assumed more attribution-critical). Non-object / invalid inputs and
// already-fitting objects are returned unchanged.
export const limitSerializedObjectSize = (obj, limit, protectedKeys = []) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)
    || typeof limit !== 'number' || limit < 0) {
    return obj;
  }
  const result = { ...obj };
  const protectedSet = new Set(protectedKeys);
  const measure = (o) => JSON.stringify(o).length;
  // Serialized contribution of a single entry, i.e. `"key":value` plus the
  // separating comma, matching how it adds to the object's JSON length.
  // `JSON.stringify` returns `undefined` (not a string) for values it omits
  // from object output (`undefined` / functions / symbols); those add nothing to
  // the measured size, so report 0 rather than reading `.length` off `undefined`.
  const entrySize = (key) => {
    const serializedValue = JSON.stringify(result[key]);
    if (typeof serializedValue !== 'string') return 0;
    return JSON.stringify(key).length + serializedValue.length + 2;
  };

  while (measure(result) > limit) {
    let dropKey = null;
    let maxLen = 0;
    Object.keys(result).forEach((key) => {
      if (protectedSet.has(key)) return;
      const len = entrySize(key);
      // Only consider keys that actually contribute to the serialized size.
      // `>=` so that, among equal-length entries, the later-inserted one wins.
      if (len > 0 && len >= maxLen) {
        maxLen = len;
        dropKey = key;
      }
    });
    if (dropKey === null) break; // only protected / zero-size keys remain
    delete result[dropKey];
  }
  return result;
};

export const rwGetReferrerWithoutQueryString = (referrerURL) => {
  let url = referrerURL;

  if (url && url.trim().length > 0) {
    url = url.trim();

    if (url.startsWith('http://')) {
      url = url.slice(7);
    }

    if (url.startsWith('https://')) {
      url = url.slice(8);
    }

    if (url.startsWith('www.')) {
      url = url.slice(4);
    }

    const queryIndex = url.indexOf('?');
    if (queryIndex !== -1) {
      url = url.slice(0, queryIndex);
    }
  }

  return url;
};

export function rwGetCleanedDomainNameFromURL(url) {
  if (!url || typeof url !== 'string') return '';

  try {
    const parsedUrl = new URL(
      url.startsWith('http') ? url : `https://${url}`
    );

    const hostname = parsedUrl.hostname
      .replace(/^www\./i, '')
      .toLowerCase();

    const parts = hostname.split('.');

    // ✅ Handle localhost or single-part domains
    if (parts.length === 1) {
      return parts[0].replace(/[^a-z0-9]/gi, '');
    }

    // Handle IP addresses (optional but recommended)
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
      return hostname;
    }

    // Handle common multi-level TLDs
    const commonSecondLevelTLDs = ['co', 'ac', 'gov', 'edu', 'org', 'net'];

    const secondLast = parts[parts.length - 2];

    const domain = commonSecondLevelTLDs.includes(secondLast)
      ? parts[parts.length - 3]
      : secondLast;

    return domain.replace(/[^a-z0-9]/gi, '');
  } catch {
    return '';
  }
}

export const rwGetDomain = (strURL) => {
  let url = strURL;
  if (url != null && url.trim().length > 0) {
    if (url.indexOf('/') !== -1) {
      url = url.substring(0, url.indexOf('/'));
    }
  }
  return url;
};

export const rwStringUpto100 = (data) => {
  if (data && data != null && data.length > 100) {
    const length = 100;
    return data.substring(0, length);
  }
  return data;
};

// Strips http:// / https:// scheme, the query string, and the hash fragment
// from a URL while preserving the `www.` prefix. Falsy / non-string inputs are
// passed through unchanged. This is used to compact `*_url` attribution fields
// before they are saved to the userContext / userContextPersistent cookies so
// the cookie payload stays small.
export const rwStripUrlSchemaAndParams = (url) => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  let stripped = url;

  if (stripped.startsWith('http://')) {
    stripped = stripped.slice(7);
  } else if (stripped.startsWith('https://')) {
    stripped = stripped.slice(8);
  }

  const queryIndex = stripped.indexOf('?');
  if (queryIndex !== -1) {
    stripped = stripped.slice(0, queryIndex);
  }

  const hashIndex = stripped.indexOf('#');
  if (hashIndex !== -1) {
    stripped = stripped.slice(0, hashIndex);
  }

  return stripped;
};

// Generic sanitizer for cookie payload objects: returns a shallow copy of the
// input with every key ending in `_url` passed through
// rwStripUrlSchemaAndParams and then rwStringUpto100. Non-object / falsy
// inputs are passed through unchanged. The input object is NOT mutated.
export const rwSanitizeUrlFields = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }

  return Object.keys(data).reduce((acc, key) => {
    if (key.endsWith('_url')) {
      acc[key] = rwStringUpto100(rwStripUrlSchemaAndParams(data[key]));
    } else {
      acc[key] = data[key];
    }
    return acc;
  }, {});
};

export const isNull = (value) => value === undefined || value === null || value === '';

export const rwFetchParentDomain = (domainName) => {
  let domain = domainName;
  if (!domain || typeof domain !== 'string') return domain;

  domain = domain.trim().toLowerCase();

  // Handle localhost
  if (domain === 'localhost') return domain;

  // Handle IPv4
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(domain)) return domain;

  const parts = domain.split('.');
  if (parts.length <= 2) return domain;

  // Common 2-level TLDs
  const commonSecondLevelTLDs = [
    'co.uk',
    'org.uk',
    'gov.uk',
    'ac.uk',
    'co.in',
    'com.au',
    'net.au',
    'co.nz',
    'com.sg'
  ];

  const lastTwo = parts.slice(-2).join('.');
  const lastThree = parts.slice(-3).join('.');

  return commonSecondLevelTLDs.includes(lastTwo)
    ? lastThree
    : lastTwo;
};

// function rwEqualTo(data, regex) {
//   if (data != null && data.length > 0 && regex != null && regex.length > 0) {
//     const matcherResult = data.match(new RegExp(regex, 'i'));
//     return matcherResult !== null && matcherResult.length > 0;
//   }
//   return false;
// }

// AI assistant / chatbot referrers. GA4 added a native "AI Assistant" default
// channel on 2026-05-13 that tags these referrers with medium=`ai-assistant`
// (the source stays the referrer host, e.g. `chatgpt.com`) instead of leaving
// them in generic `referral`. We mirror that convention here so our first-party
// attribution (web_source/web_medium -> utm_source/utm_medium -> Salesforce /
// Act-On / HubSpot) agrees with GA4 and AI-sourced leads get their own bucket
// rather than being lumped into `referral` (or `direct` once the round-trip
// runs). The list intentionally extends GA4's native set (ChatGPT, Gemini,
// Copilot, DeepSeek, Grok) to also cover the high-intent assistants GA4 still
// leaves in Referral (Perplexity, Claude, Meta AI, You.com, Mistral) -- i.e. it
// behaves like the recommended GA4 *custom* channel group, which is more
// complete than the native one. Matching is by hostname (exact host or a
// subdomain of it) and, critically, runs BEFORE the search-engine check below,
// because some AI hosts are subdomains of search engines (e.g.
// `gemini.google.com` would otherwise match `google` and be mislabeled
// `organic`). Keep in sync with Google's published AI Assistants list.
export const AI_ASSISTANT_HOSTS = [
  'chatgpt.com',
  'chat.openai.com',
  'gemini.google.com',
  'bard.google.com',
  'copilot.microsoft.com',
  'deepseek.com',
  'grok.com',
  'x.ai',
  'perplexity.ai',
  'claude.ai',
  'meta.ai',
  'you.com',
  'mistral.ai'
];

// True when `value` denotes a known AI-assistant host. Accepts a bare host
// (e.g. a `utm_source` value like `chatgpt.com`), a hostname, or a full URL /
// referrer string. Matching is on the host only (exact host or a subdomain of
// a known host). Used both to classify referrers (detectReferrerInitials) and
// to recover the `ai-assistant` medium from a `utm_source` when the referrer
// was stripped (resolveAiAssistantMedium).
export const isAiAssistantHost = (value) => {
  if (!value || typeof value !== 'string') return false;
  let host = value.trim().toLowerCase();
  if (host.startsWith('http://')) host = host.slice(7);
  else if (host.startsWith('https://')) host = host.slice(8);
  host = host.replace(/^www\./, '');
  const slashIndex = host.indexOf('/');
  if (slashIndex !== -1) host = host.slice(0, slashIndex);
  const queryIndex = host.indexOf('?');
  if (queryIndex !== -1) host = host.slice(0, queryIndex);
  if (!host) return false;
  return AI_ASSISTANT_HOSTS.some((aiHost) => host === aiHost || host.endsWith(`.${aiHost}`));
};

export const detectReferrerInitials = (referrer) => {
  const searchEngines = [
    'google', 'bing', 'baidu', 'yahoo', 'duckduckgo', 'naver', 'ecosia.org',
    'yandex', 'sogou', 'so.com', 'qwant.com', 'search.smt.docomo', 'go.mail.ru',
    'ask', 'goo.ne', 'daum', 'rakuten', 'biglobe', 'seznam', 'aol', 'kvasir',
    '360.cn', 'onet', 'avg', 'centrum.cz', 'lycos', 'rambler', 'virgilio',
    'auone', 'babylon', 'search-results', 'startsiden'
  ];

  const initials = {
    web_source: 'direct',
    web_medium: 'direct'
  };

  // ignore empty referrer or internal referrer
  if (!referrer || referrer.includes('splashtop.c')) {
    return initials;
  }

  const referrerLower = referrer.toLowerCase();

  // Parse the referrer hostname once (sans a leading "www."). Shared by the
  // AI-assistant and referral classifications below. Falls back to '' when the
  // referrer is not a parseable absolute URL, in which case those hostname-based
  // branches are skipped (search-engine substring matching still runs).
  let hostname = '';
  try {
    hostname = new URL(referrer).hostname.replace(/^www\./, '').toLowerCase();
  } catch (e) {
    hostname = '';
  }

  // Detect AI-assistant traffic (must run before the search-engine check --
  // see AI_ASSISTANT_HOSTS note above). web_source keeps the full host, e.g.
  // `chatgpt.com`, so downstream reports read `chatgpt.com / ai-assistant`.
  if (hostname && isAiAssistantHost(hostname)) {
    initials.web_source = hostname;
    initials.web_medium = 'ai-assistant';
    return initials;
  }

  // Detect search engine traffic
  const matchedEngine = searchEngines.find((engine) => referrerLower.includes(engine));
  if (matchedEngine) {
    initials.web_source = matchedEngine;
    initials.web_medium = 'organic';
    return initials;
  }

  // Otherwise treat as referral. Use the full referrer hostname (sans an
  // optional leading "www.") as web_source so downstream attribution fields
  // preserve the originating host -- e.g. search.brave.com stays
  // "search.brave.com" rather than collapsing to "search", and
  // statics.teams.cdn.office.net stays the full host rather than "statics".
  if (hostname) {
    initials.web_source = hostname;
    initials.web_medium = 'referral';
  } else {
    // fallback if URL parsing fails
    initials.web_source = 'unknown';
    initials.web_medium = 'referral';
  }

  return initials;
};

// Recover the `ai-assistant` medium when the traffic SOURCE is a known AI
// assistant but the MEDIUM was not classified as such. AI tools (notably
// ChatGPT) append `utm_source=chatgpt.com` to citation links while frequently
// stripping `document.referrer` (mobile / in-app WebViews, `rel=noreferrer` on
// paid tiers, clipboard copy-paste). With no referrer, detectReferrerInitials
// returns `direct`, so a lead lands as `chatgpt.com / direct` even though the
// `utm_source` clearly identifies an AI assistant. When the source is a known
// AI host, upgrade the medium to `ai-assistant` -- but only when the incoming
// medium is a non-authoritative default (empty or `direct`). Explicit campaign
// mediums (`cpc`, `organic`, `referral`, ...) and the `disabled` consent
// sentinel are preserved so paid/organic attribution and the consent fallback
// are never masked.
export const resolveAiAssistantMedium = (source, medium) => {
  if (!isAiAssistantHost(source)) return medium;
  if (isNull(medium) || medium === 'direct') return 'ai-assistant';
  return medium;
};

const sendGAEvent = (category, action, label, extraProps = {}) => {
  // right now, there's only social media & form events
  if (isBrowser && typeof rudderanalytics !== 'undefined' && typeof rudderanalytics.track === 'function') {
    // if it's flexible form, then send 'Flexible form' string and return
    if (!!category && typeof category === 'string' && category === 'Flexible Form') {
      const flexibleFormPayload = {
        Form_Variation: action,
        Form_Event: label,
        ...(extraProps?.abTests != null && { Form_ABTests: extraProps.abTests }),
        ...(extraProps?.region != null && extraProps.region !== '' && { Form_Region: extraProps.region }),
        // Product(s) chosen — attached to the `success` send (see sendGAFormEvent in
        // trialFormUtils.js). Single value for non-checkbox forms; comma-separated for the
        // checkbox multi-select picker.
        ...(extraProps?.selectedProducts != null && extraProps.selectedProducts !== '' && {
          Form_Selected_Products: extraProps.selectedProducts
        }),
        // Viewport metrics captured at "form loaded" time (TrialForm /
        // TrialFormSteps use measureFormViewportMetrics in trialFormUtils.js).
        // GA4 only records screen size, not window size, so these let analytics
        // see the real browser viewport the user had, plus whether the submit
        // button was on screen at load without the user scrolling. Sent as GA4
        // event params through the GTM Flexible_Form tag.
        ...(typeof extraProps?.windowInnerHeight === 'number' && {
          Form_Window_Inner_Height: extraProps.windowInnerHeight
        }),
        ...(typeof extraProps?.windowInnerWidth === 'number' && {
          Form_Window_Inner_Width: extraProps.windowInnerWidth
        }),
        ...(typeof extraProps?.submitVisibleOnLoad === 'boolean' && {
          Form_Submit_Visible_On_Load: extraProps.submitVisibleOnLoad
        }),
        ...(typeof extraProps?.submitButtonTopOnLoad === 'number' && {
          Form_Submit_Button_Top_On_Load: extraProps.submitButtonTopOnLoad
        }),
      };
      const flexibleFormLog = { event: 'Flexible_Form', ...flexibleFormPayload };
      rudderanalytics.track('Flexible_Form', flexibleFormPayload);
      // Log the object as a single argument so DevTools "Copy object" resolves it (a string
      // label + object can yield null), plus a JSON string so the full payload — including
      // Form_Selected_Products — is visible/copyable without the inline preview "…" truncation.
      console.log('Sending GA event:', flexibleFormLog);
      console.log('Sending GA event (json):', JSON.stringify(flexibleFormLog));
      return;
    }

    // Handle strings
    if (typeof category === 'string') {
      rudderanalytics.track(category);
      console.log('Sending GA event: ', {
        event: category
      });
      return;
    }

    // Handle objects like { event: 'foxpass_free_trial_click', properties: {...} }
    // where an arbitrary properties object is forwarded as the Rudder track
    // payload (mapped to GA4 event params by the destination).
    if (typeof category === 'object' && category !== null && 'event' in category && 'properties' in category) {
      rudderanalytics.track(category.event, category.properties);
      console.log('Sending GA event: ', {
        event: category.event,
        ...category.properties
      });
      return;
    }

    // Handle objects like { event: 'Form_Submit', path: 'example' }
    if (typeof category === 'object' && category !== null && 'event' in category && 'path' in category) {
      rudderanalytics.track(category.event, { path: category.path });
      console.log('Sending GA event: ', {
        event: category.event,
        path: category.path
      });
      return;
    }

    // Handle objects like { event: 'Form_Submit' }
    // or { event: `social-share-click-${network}` }
    if (typeof category === 'object' && category !== null && 'event' in category && !('path' in category)) {
      rudderanalytics.track(category.event);
      console.log('Sending GA event: ', {
        event: category.event
      });
    }
  } else {
    console.log('rudderanalytics not there yet:::');
  }
};

// Window during which a duplicate (category, action, label) call is suppressed.
export const SEND_TO_GTM_DEDUPE_MS = 300;
// Interval used to poll for rudderanalytics readiness when it has not yet loaded.
const RUDDER_READY_POLL_MS = 300;
// Safety cap on how long we keep polling for rudderanalytics before giving up
// (~9s). Keeps a stuck/blocked SDK from accumulating timers indefinitely.
const RUDDER_READY_POLL_TIMEOUT_MS = 9000;

const recentSendToGtmEvents = new Map();

const buildSendToGtmEventKey = (category, action, label) => {
  let categoryKey;
  if (typeof category === 'string') {
    categoryKey = category;
  } else if (category && typeof category === 'object' && 'event' in category) {
    categoryKey = `${category.event}|${category.path ?? ''}`;
  } else {
    try {
      categoryKey = JSON.stringify(category);
    } catch (e) {
      categoryKey = String(category);
    }
  }
  return `${categoryKey}|${action}|${label}`;
};

const isRudderAnalyticsReady = () => (
  isBrowser
  && typeof rudderanalytics !== 'undefined'
  && typeof rudderanalytics.track === 'function'
);

// Test-only hook: clears the dedupe map so independent tests don't see each
// other's events. Not part of the public runtime contract.
export const __resetSendToGtmDedupeForTests = () => {
  recentSendToGtmEvents.clear();
};

// Fires the analytics event immediately when rudderanalytics is available,
// otherwise polls every 300ms until it is (or until ~9s elapse). Identical
// (category, action, label) calls within SEND_TO_GTM_DEDUPE_MS are suppressed
// to avoid double-counting from rapid re-renders or chained handlers. Each
// call gets its own retry timer, so concurrent callers cannot clobber each
// other's pending events the way a shared setInterval did previously.
//
// Returns a promise resolving to whether the event reached rudderanalytics, so
// callers that persist a "already counted" marker (e.g. the thank-you page
// conversion in Layout.js) can keep retrying when the SDK never loads. Most
// callers can ignore the return value.
export const sendToGtm = (category, action = '', label = '', extraProps = {}) => {
  if (!isBrowser) return Promise.resolve(false);

  const key = buildSendToGtmEventKey(category, action, label);
  const now = Date.now();
  const last = recentSendToGtmEvents.get(key);
  if (last !== undefined && now - last < SEND_TO_GTM_DEDUPE_MS) {
    // An identical event is already sent or in flight, so from the caller's
    // point of view this one is accounted for.
    return Promise.resolve(true);
  }
  recentSendToGtmEvents.set(key, now);

  if (isRudderAnalyticsReady()) {
    sendGAEvent(category, action, label, extraProps);
    return Promise.resolve(true);
  }

  const startedAt = now;
  return new Promise((resolve) => {
    const retryId = setInterval(() => {
      if (isRudderAnalyticsReady()) {
        clearInterval(retryId);
        sendGAEvent(category, action, label, extraProps);
        resolve(true);
      } else if (Date.now() - startedAt >= RUDDER_READY_POLL_TIMEOUT_MS) {
        clearInterval(retryId);
        resolve(false);
      }
    }, RUDDER_READY_POLL_MS);
  });
};

/**
 * Send a Flexible_Form event immediately (no 300ms interval).
 * Use when the event must not be overwritten by a following sendToGtm.
 */
export const sendFlexibleFormEventImmediate = (action, label, extraProps = {}) => {
  sendGAEvent('Flexible Form', action, label, extraProps);
};

export const rudderIdentify = (values, response) => {
  if (isBrowser
    && typeof rudderanalytics !== 'undefined'
    && typeof rudderanalytics.identify === 'function') {
    // if values are present and form is submitted, then identify the user
    if (Object.keys(values).length > 0
    && values.first_name?.length > 0
    && values.last_name?.length > 0
    && values.email?.length > 0
    && typeof response === 'object') {
      rudderanalytics.identify({
        firstName: values.first_name,
        lastName: values.last_name,
        email: values.email,
        phone: values.phone || '',
      });
    }
  }
};

export const rudderIdentifyHubspot = (values) => {
  if (isBrowser
    && typeof rudderanalytics !== 'undefined'
    && typeof rudderanalytics.identify === 'function'
    && values
    && Object.keys(values).length > 0) {
    console.log('rudderIdentifyHubspot: identify payload', values);
    rudderanalytics.identify(values);
  }
};

export const contentfulEditUrl = (entryId, compose = false) => {
  const spaceId = process.env.GATSBY_CONTENTFUL_SPACE_ID;
  let environment = process.env.GATSBY_CONTENTFUL_ENVIRONMENT;

  // Override for web-test to use master environment
  if (environment === 'web-test') environment = 'master';

  if (compose) {
    return `https://compose.contentful.com/spaces/${spaceId}/environments/${environment}/page/${entryId}`;
  }

  return `https://app.contentful.com/spaces/${spaceId}/environments/${environment}/entries/${entryId}`;
};

export const redirectIfEdit = (entryId, composeEditor = false) => {
  if (isBrowser && window.location.search.indexOf('edit') > -1) {
    const urlParams = new URLSearchParams(window.location.search);
    const editToken = urlParams.get('edit');

    if (editToken !== null) {
      const url = contentfulEditUrl(entryId, composeEditor);
      window.location.href = url;
    }
  }
};

export const setOsanoParam = (data, param, status) => {
  const denied = (status?.ANALYTICS === 'DENY');
  const docReferrerLength = document?.referrer?.length;
  const beenDisabled = (status?.beenDisabled === true);
  const disableParam = (beenDisabled === true
  && docReferrerLength < 1)
    ? 'true' : 'false';
  let paramString;
  if (param === 'page') {
    /* eslint-disable-next-line no-nested-ternary */
    paramString = denied ? 'disabled' : data.page;
  } else if (param === 'web_source') {
    paramString = (disableParam === 'true') ? 'disabled' : data.web_source;
  } else if (param === 'web_medium') {
    paramString = (disableParam === 'true') ? 'disabled' : data.web_medium;
  } else if (param === 'utm_source') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_source || data.web_source);
  } else if (param === 'utm_medium') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_medium || data.web_medium);
  } else if (param === 'utm_device') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_device || data.web_device);
  } else if (param === 'utm_content') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_content || data.web_content);
  } else if (param === 'utm_campaign') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_campaign || data.web_campaign);
  } else if (param === 'utm_term') {
    paramString = (disableParam === 'true') ? 'disabled' : (data.utm_term || data.web_term);
  }

  return paramString;
};

// Recursive function to highlight search terms in rich text
export const recursiveHighlight = (block, searchRegex) => {
  if (block.nodeType === 'text') {
    const matches = block.value.match(searchRegex);

    if (matches !== null) {
      const wrappingText = block.value.split(searchRegex);

      const highlightedMatches = wrappingText.reduce((acc, match, index) => {
        const newData = [
          ...acc,
          {
            nodeType: 'text',
            value: match,
            data: block.data,
            marks: block.marks
          }
        ];

        if (index !== wrappingText.length - 1) {
          newData.push({
            nodeType: 'text',
            value: matches[index],
            data: block.data,
            marks: [
              ...block.marks,
              { type: 'highlight' }
            ]
          });
        }

        return newData;
      }, []);

      return highlightedMatches;
    }

    return block;
  }

  if (block.content) {
    return {
      ...block,
      content: block.content.reduce((acc, contentBlock) => {
        const highlightedBlock = recursiveHighlight(contentBlock, searchRegex);

        if (Array.isArray(highlightedBlock)) {
          return [...acc, ...highlightedBlock];
        }

        return [...acc, highlightedBlock];
      }, [])
    };
  }

  return block;
};

export const loadZoomInfo = () => {
  // Load ZoomInfo script once when form is ready
  if (isBrowser && !window.ZIProjectKey) {
    window.ZIProjectKey = 'e91c6e93001680557568';
    const zi = document.createElement('script');
    zi.type = 'text/javascript';
    zi.async = true;
    zi.src = 'https://js.zi-scripts.com/zi-tag.js';

    // Add load event listener
    zi.onload = () => {
      console.log('✅ ZoomInfo script loaded successfully');
      console.log('🔍 Checking for ZoomInfo objects:', {
        zitag: !!window.zitag,
        ZILogs: !!window.ZILogs,
        ZIProjectKey: window.ZIProjectKey
      });

      // Check if ZoomInfo is working after a short delay
      setTimeout(() => {
        if (window.zitag && typeof window.zitag.GetListOfEntitlements === 'function') {
          console.log('✅ ZoomInfo functions are available');
        } else {
          console.warn('⚠️ ZoomInfo functions not available yet');
        }
      }, 1000);
    };

    // Add error event listener
    zi.onerror = (error) => {
      console.error('❌ Failed to load ZoomInfo script:', error);
      console.error('Script URL:', zi.src);
    };

    if (document.readyState === 'complete') {
      document.body.appendChild(zi);
      console.log('📄 Document ready, appending ZoomInfo script');
    } else {
      window.addEventListener('load', () => {
        document.body.appendChild(zi);
        console.log('📄 Window loaded, appending ZoomInfo script');
      });
    }
    console.log('🚀 Loading ZoomInfo FormComplete for Hubspot form');
  } else {
    console.log('ℹ️ ZoomInfo script already loaded or ZIProjectKey exists');
  }
};
